import { useEffect, useState } from "react";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import { Download, Filter as FilterIcon, RefreshCw } from "lucide-react";
import { LEVEL_STYLES } from "@/lib/enums";
import { UserPasswordDialog } from "@/components/UserPasswordDialog";

const TABS = [
  { id: "readings", label: "Readings" },
  { id: "users", label: "Users" },
  { id: "screenings", label: "Screenings" },
  { id: "applications", label: "Applications" },
  { id: "library-lends", label: "Library Lends" },
  { id: "mitigations", label: "Mitigations" },
];

const APP_STATUSES = ["submitted", "in_review", "approved", "denied", "completed"];

const errorMessage = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message || "Request failed";

const csvDownload = async (path, filename) => {
  const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) throw new Error(`Export failed (HTTP ${r.status})`);
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const buildQuery = (filters) => {
  const q = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== "" && v !== undefined && v !== null) q.set(k, v);
  });
  return q.toString();
};

const AdminDataBrowser = () => {
  const [tab, setTab] = useState("readings");
  const [filters, setFilters] = useState({});
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const [data, setData] = useState({ total: 0, rows: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [pwUser, setPwUser] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilters((prev) => (buildQuery(prev) === buildQuery(filters) ? prev : filters)), 300);
    return () => clearTimeout(t);
  }, [filters]);

  // Abort the in-flight request when tab/filters change so stale results never land.
  useEffect(() => {
    const ctrl = new AbortController();
    const qs = buildQuery(debouncedFilters);
    setLoading(true);
    setError("");
    api.get(`/admin/data/${tab}${qs ? "?" + qs : ""}`, { signal: ctrl.signal })
      .then(({ data: d }) => setData(d))
      .catch((e) => { if (!ctrl.signal.aborted) setError(errorMessage(e)); })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [tab, debouncedFilters, reloadKey]);

  const load = () => setReloadKey((k) => k + 1);

  const switchTab = (id) => {
    if (id === tab) return;
    setTab(id);
    setFilters({});
    setDebouncedFilters({});
    setData({ total: 0, rows: [] });
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const updateStatus = async (appId, status) => {
    const form = new FormData();
    form.append("status", status);
    try {
      await api.patch(`/admin/data/applications/${appId}`, form);
    } catch (e) {
      setError(errorMessage(e));
    }
    load();
  };

  const exportCsv = async () => {
    setExporting(true);
    setError("");
    try {
      await csvDownload(`/admin/data/${tab}/export.csv?${buildQuery(debouncedFilters)}`, `${tab}.csv`);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="card-flat fade-up delay-6" data-testid="admin-data-browser">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <p className="eyebrow">Data browser</p>
          <h3 className="font-serif text-xl mt-1">All submissions</h3>
        </div>
        <button onClick={load} className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="admin-data-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-[#E8ECEA] pb-1 mb-4" data-testid="admin-data-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => switchTab(t.id)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-t-xl transition-colors ${
              tab === t.id ? "bg-[#2F524A] text-white" : "text-[#5C6661] hover:text-[#1C201F]"
            }`}
            data-testid={`admin-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-4 p-3 rounded-xl bg-[#F4F7F6]" data-testid="admin-filters">
        <FilterIcon size={14} className="text-[#5C6661] mb-1.5" />
        {tab === "readings" && (
          <>
            <FilterInput label="State (2-letter)" value={filters.state || ""} onChange={(v) => setF("state", v.toUpperCase())} tid="filter-state" />
            <FilterInput label="ZIP" value={filters.zip_code || ""} onChange={(v) => setF("zip_code", v)} tid="filter-zip" />
            <FilterInput label="Min pCi/L" type="number" value={filters.min_pci || ""} onChange={(v) => setF("min_pci", v)} tid="filter-min" />
            <FilterInput label="Max pCi/L" type="number" value={filters.max_pci || ""} onChange={(v) => setF("max_pci", v)} tid="filter-max" />
            <FilterInput label="From" type="date" value={filters.from_date || ""} onChange={(v) => setF("from_date", v)} tid="filter-from" />
            <FilterInput label="To" type="date" value={filters.to_date || ""} onChange={(v) => setF("to_date", v)} tid="filter-to" />
            <FilterSelect label="Kind" value={filters.kind || ""} onChange={(v) => setF("kind", v)} tid="filter-kind"
              options={[["", "Any"], ["screening", "Screening"], ["confirmatory", "Confirmatory"], ["post_mitigation", "Post-mitigation"], ["long_term", "Long-term"]]} />
          </>
        )}
        {tab === "users" && (
          <>
            <FilterSelect label="Type" value={filters.user_type || ""} onChange={(v) => setF("user_type", v)} tid="filter-user-type"
              options={[["", "Any"], ["home", "Home"], ["school", "School"], ["library", "Library"], ["workplace", "Workplace"], ["assistance", "Assistance"], ["community", "Community"]]} />
            <FilterSelect label="Role" value={filters.role || ""} onChange={(v) => setF("role", v)} tid="filter-role"
              options={[["", "Any"], ["user", "User"], ["admin", "Admin"]]} />
          </>
        )}
        {tab === "screenings" && (
          <>
            <FilterInput label="State" value={filters.state || ""} onChange={(v) => setF("state", v.toUpperCase())} tid="filter-state" />
            <FilterSelect label="Qualifies" value={filters.qualifies ?? ""} onChange={(v) => setF("qualifies", v)} tid="filter-qualifies"
              options={[["", "Any"], ["true", "Yes"], ["false", "No"]]} />
          </>
        )}
        {tab === "applications" && (
          <FilterSelect label="Status" value={filters.status || ""} onChange={(v) => setF("status", v)} tid="filter-status"
            options={[["", "Any"], ...APP_STATUSES.map((s) => [s, s])]} />
        )}
        {tab === "mitigations" && (
          <>
            <FilterInput label="State" value={filters.state || ""} onChange={(v) => setF("state", v.toUpperCase())} tid="filter-state" />
            <FilterInput label="Contractor" value={filters.contractor || ""} onChange={(v) => setF("contractor", v)} tid="filter-contractor" />
          </>
        )}
        <button
          onClick={exportCsv}
          disabled={exporting}
          className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1 ml-auto disabled:opacity-60"
          data-testid="admin-data-export"
        >
          <Download size={12} /> {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {error && (
        <div className="text-xs text-[#9B2C2C] bg-[#FCEDED] border border-[#F5C2C2] rounded-lg px-3 py-2 mb-3" data-testid="admin-data-error">
          {error}
        </div>
      )}

      <p className="text-xs text-[#5C6661] mb-2" data-testid="admin-data-count">
        Showing {data.rows.length} of {data.total} · {loading ? "loading…" : ""}
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="admin-data-table">
          <thead>
            <tr className="text-left text-[10px] tracking-widest uppercase text-[#5C6661] border-b border-[#E8ECEA]">
              {tab === "readings" && ["Date", "pCi/L", "Level", "Kind", "Location", "User", "State/ZIP"].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
              {tab === "users" && ["Signed up", "Name / Email", "Type", "Role", "Property", "# Readings", "Mitigation", "Password"].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
              {tab === "screenings" && ["Submitted", "User", "State/ZIP", "pCi/L", "Income", "Household", "Qualifies"].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
              {tab === "applications" && ["Submitted", "Name", "Contact", "Address", "Best time", "Proof", "Status"].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
              {tab === "library-lends" && ["Created", "Library", "Monitor", "Borrow date", "User", "Consent"].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
              {tab === "mitigations" && ["Completed", "Contractor", "System", "Cost", "Pre → Post", "Reduction", "State/ZIP", "User"].map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {tab === "readings" && data.rows.map((r) => {
              const level = r.classification?.level || "below-action";
              const s = LEVEL_STYLES[level];
              return (
                <tr key={r.id} className="border-b border-[#E8ECEA]/60" data-testid="admin-row">
                  <td className="py-2 pr-3">{r.reading_date}</td>
                  <td className="py-2 pr-3 font-serif">{r.pci_l}</td>
                  <td className="py-2 pr-3"><span className="text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.text }}>{r.classification?.label || "—"}</span></td>
                  <td className="py-2 pr-3">{r.kind}</td>
                  <td className="py-2 pr-3">{r.location_in_home}</td>
                  <td className="py-2 pr-3">{r.user_name}<br /><span className="text-[10px] text-[#5C6661]">{r.user_email}</span></td>
                  <td className="py-2 pr-3">{r.property_state} {r.property_zip}</td>
                </tr>
              );
            })}
            {tab === "users" && data.rows.map((u) => (
              <tr key={u.id} className="border-b border-[#E8ECEA]/60" data-testid="admin-row">
                <td className="py-2 pr-3">{u.created_at?.slice(0, 10)}</td>
                <td className="py-2 pr-3">{u.name}<br /><span className="text-[10px] text-[#5C6661]">{u.email}</span></td>
                <td className="py-2 pr-3">{u.user_type || "home"}</td>
                <td className="py-2 pr-3">{u.role}</td>
                <td className="py-2 pr-3">{u.property ? `${u.property.state} ${u.property.zip_code}` : "—"}</td>
                <td className="py-2 pr-3">{u.readings_count}</td>
                <td className="py-2 pr-3">{u.has_mitigation ? "✓" : "—"}</td>
                <td className="py-2 pr-3">
                  {u.role === "admin" ? "—" : (
                    <button onClick={() => setPwUser(u)} className="text-[#2F524A] font-semibold hover:underline"
                      data-testid={`admin-reset-password-${u.id}`}>Reset</button>
                  )}
                </td>
              </tr>
            ))}
            {tab === "screenings" && data.rows.map((r) => (
              <tr key={r.id} className="border-b border-[#E8ECEA]/60" data-testid="admin-row">
                <td className="py-2 pr-3">{r.submitted_at?.slice(0, 10)}</td>
                <td className="py-2 pr-3">{r.user_name}<br /><span className="text-[10px] text-[#5C6661]">{r.user_email}</span></td>
                <td className="py-2 pr-3">{r.state} {r.zip_code}</td>
                <td className="py-2 pr-3">{r.latest_pci_l}</td>
                <td className="py-2 pr-3">{r.income_band}</td>
                <td className="py-2 pr-3">{r.household_size}</td>
                <td className="py-2 pr-3">{r.evaluation?.qualifies ? "✅ Yes" : "❌ No"}</td>
              </tr>
            ))}
            {tab === "applications" && data.rows.map((r) => (
              <tr key={r.id} className="border-b border-[#E8ECEA]/60" data-testid="admin-row">
                <td className="py-2 pr-3">{r.submitted_at?.slice(0, 10)}</td>
                <td className="py-2 pr-3">{r.contact_name}<br /><span className="text-[10px] text-[#5C6661]">{r.user_email}</span></td>
                <td className="py-2 pr-3">{r.phone}</td>
                <td className="py-2 pr-3">{r.address}</td>
                <td className="py-2 pr-3">{r.best_time}</td>
                <td className="py-2 pr-3">{r.proof?.filename || "—"}</td>
                <td className="py-2 pr-3">
                  <select value={r.status || "submitted"} onChange={(e) => updateStatus(r.id, e.target.value)}
                    className="text-xs border border-[#E8ECEA] rounded px-1.5 py-0.5 bg-white" data-testid="admin-app-status">
                    {APP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {tab === "library-lends" && data.rows.map((r) => (
              <tr key={r.id} className="border-b border-[#E8ECEA]/60" data-testid="admin-row">
                <td className="py-2 pr-3">{r.created_at?.slice(0, 10)}</td>
                <td className="py-2 pr-3">{r.library_name}</td>
                <td className="py-2 pr-3">{r.monitor_id}</td>
                <td className="py-2 pr-3">{r.borrow_date}</td>
                <td className="py-2 pr-3">{r.user_name}<br /><span className="text-[10px] text-[#5C6661]">{r.user_email}</span></td>
                <td className="py-2 pr-3">{r.consent_share_data ? "✓" : "—"}</td>
              </tr>
            ))}
            {tab === "mitigations" && data.rows.map((r) => (
              <tr key={r.id} className="border-b border-[#E8ECEA]/60" data-testid="admin-row">
                <td className="py-2 pr-3">{r.completed_date}</td>
                <td className="py-2 pr-3">{r.contractor}</td>
                <td className="py-2 pr-3">{r.system_type || "—"}</td>
                <td className="py-2 pr-3">{r.cost_usd ? `$${r.cost_usd.toLocaleString()}` : "—"}</td>
                <td className="py-2 pr-3">
                  {r.pre_pci != null && r.post_pci != null ? `${r.pre_pci} → ${r.post_pci}` : "—"}
                </td>
                <td className="py-2 pr-3">{r.reduction_pct != null ? `${r.reduction_pct}%` : "—"}</td>
                <td className="py-2 pr-3">{r.property_state} {r.property_zip}</td>
                <td className="py-2 pr-3">{r.user_name}</td>
              </tr>
            ))}
            {data.rows.length === 0 && !loading && !error && (
              <tr><td colSpan={8} className="text-center text-[#5C6661] py-6">No records match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <UserPasswordDialog user={pwUser} onClose={() => setPwUser(null)} />
    </div>
  );
};

const FilterInput = ({ label, value, onChange, type = "text", tid }) => (
  <label className="text-[10px] tracking-widest uppercase text-[#5C6661]">
    <span className="block mb-0.5">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="text-xs border border-[#E8ECEA] rounded-lg px-2 py-1 bg-white w-28"
      data-testid={tid} />
  </label>
);

const FilterSelect = ({ label, value, onChange, options, tid }) => (
  <label className="text-[10px] tracking-widest uppercase text-[#5C6661]">
    <span className="block mb-0.5">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="text-xs border border-[#E8ECEA] rounded-lg px-2 py-1 bg-white"
      data-testid={tid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

export default AdminDataBrowser;
